import { createFormContext, FormErrors } from '@mantine/form';
import { useRef } from 'react';

export interface ValidationFieldValues {
  key: number;
  organic: 'oui' | 'non';
  last_organic?: Date | null;
  is_validated: boolean;
}

interface ValidationFormValues {
  fields: ValidationFieldValues[];
}

export function isFieldAnswerComplete(
  field: ValidationFieldValues | undefined,
): boolean {
  if (!field) {
    return false;
  }
  if (field.organic === 'oui') {
    return field.last_organic != null;
  }
  if (field.organic === 'non') {
    return field.is_validated;
  }
  return false;
}

export function areAllFieldAnswersComplete(
  fields: ValidationFieldValues[],
): boolean {
  return fields.length > 0 && fields.every(isFieldAnswerComplete);
}

function buildFieldAnswerErrors(
  values: ValidationFormValues,
  requiredMessage: string,
): FormErrors {
  const errors: FormErrors = {};

  values.fields.forEach((field, index) => {
    const prefix = `fields.${index}`;

    if (field.organic !== 'oui' && field.organic !== 'non') {
      errors[`${prefix}.organic`] = requiredMessage;
    }

    if (field.organic === 'oui' && !field.last_organic) {
      errors[`${prefix}.last_organic`] = requiredMessage;
    }

    if (field.organic === 'non' && !field.is_validated) {
      errors[`${prefix}.is_validated`] = requiredMessage;
    }
  });

  return errors;
}

const [ValidationFormProvider, useValidationFormContext, useForm] =
  createFormContext<ValidationFormValues>();

export const useValidationForm = (requiredMessage: string) => {
  const requiredMessageRef = useRef(requiredMessage);
  requiredMessageRef.current = requiredMessage;

  return useForm({
    initialValues: {
      fields: [] as ValidationFieldValues[],
    },
    validate: (values) =>
      buildFieldAnswerErrors(values, requiredMessageRef.current),
    validateInputOnBlur: false,
    validateInputOnChange: true,
  });
};

export type UseValidationFormReturn = ReturnType<typeof useValidationForm>;

export async function validateFieldAnswerAtIndex(
  form: UseValidationFormReturn,
  index: number,
): Promise<boolean> {
  const basePath = `fields.${index}`;

  await form.validateField(`${basePath}.organic`);
  await form.validateField(`${basePath}.last_organic`);
  await form.validateField(`${basePath}.is_validated`);

  return isFieldAnswerComplete(form.values.fields[index]);
}

export { ValidationFormProvider, useValidationFormContext };
